import React, { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useFilters } from '../99-contexts/FilterContext';
import { allData, Chantier } from './data/ServicesData';

const ResultsTable: React.FC = () => {
  const { filters } = useFilters();
  const [expandedOuvrages, setExpandedOuvrages] = useState<Set<number>>(new Set());
  const [expandedChantiers, setExpandedChantiers] = useState<Set<number>>(new Set());

    // Les données sont maintenant importées depuis ServicesData.ts

  // Fonction pour formater les prix en français
  const formatPrix = (prix: number) => {
    return prix.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Fonction améliorée pour parser les prix unitaires
  const parsePrixUnitaire = (prixString: string): number => {
    // Supprimer tous les caractères non numériques sauf le point et la virgule
    const cleanPrice = prixString.replace(/[^\d,.]/g, '');
    
    // Remplacer la virgule par un point pour la conversion
    const normalizedPrice = cleanPrice.replace(',', '.');
    
    const prix = parseFloat(normalizedPrice);
    
    // Log pour déboguer
    console.log(`🔍 Parsing prix: "${prixString}" -> "${cleanPrice}" -> "${normalizedPrice}" -> ${prix}`);
    
    return isNaN(prix) ? 0 : prix;
  };

  // Fonction pour calculer le dernier prix actualisé par unité
  const calculateLastActualizedPrice = (chantiers: Chantier[], unite: string): string => {
    // Trier les chantiers par date (le plus récent en premier) en utilisant dateChantier
    const sortedChantiers = chantiers.sort((a, b) => {
      const dateA = new Date(a.dateChantier).getTime();
      const dateB = new Date(b.dateChantier).getTime();
      return dateB - dateA; // Le plus récent en premier
    });
    
    // Prendre le premier (le plus récent) et extraire le prix actualisé
    const lastChantier = sortedChantiers[0];
    if (lastChantier.prixActualise) {
      // Extraire le prix numérique du prix actualisé (support des espaces et virgules)
      const priceStr = lastChantier.prixActualise.replace(/[^\d,.\s]/g, '').replace(/\s/g, '').replace(',', '.');
      const price = parseFloat(priceStr);
      return `${formatPrix(price)} €/${unite}`;
    } else {
      // Fallback sur le prix unitaire si pas de prix actualisé
      const priceStr = lastChantier.prixUnitaire.replace(/[^\d,.\s]/g, '').replace(/\s/g, '').replace(',', '.');
      const price = parseFloat(priceStr);
      return `${formatPrix(price)} €/${unite}`;
    }
  };

  // Filtrer les données en fonction des filtres actifs
  const filteredData = useMemo(() => {
    console.log('🔍 Filtres actifs:', filters);
    
    return allData.filter(ouvrage => {
      // Filtre par recherche textuelle
      if (filters.searchTerm && !ouvrage.intitule.toLowerCase().includes(filters.searchTerm.toLowerCase()) &&
          !ouvrage.categorie.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
        return false;
      }

      // Filtre par catégorie
      if (filters.categorie && ouvrage.categorie.toLowerCase() !== filters.categorie.toLowerCase()) {
        return false;
      }

      // Filtre par unité
      if (filters.unite && ouvrage.unite.toLowerCase() !== filters.unite.toLowerCase()) {
        return false;
      }

      // Filtrer les chantiers selon les autres critères
      const filteredChantiers = ouvrage.chantiers.filter(chantier => {
        // Filtre par localisation
        if (filters.localisation && chantier.localisation.toLowerCase() !== filters.localisation.toLowerCase()) {
          return false;
        }

        // Filtre par année
        if (filters.annee && chantier.annee !== filters.annee) {
          return false;
        }

        // Filtre par fournisseur
        if (filters.fournisseur && !chantier.fournisseur.toLowerCase().includes(filters.fournisseur.toLowerCase())) {
          return false;
        }

        // Filtre par projet
        if (filters.projet && !chantier.nom.toLowerCase().includes(filters.projet.toLowerCase())) {
          return false;
        }

        // Filtre par prix (convertir en nombre pour la comparaison)
        if (filters.prixMin || filters.prixMax) {
          const prix = parsePrixUnitaire(chantier.prixUnitaire);
          const minPrix = filters.prixMin ? parsePrixUnitaire(filters.prixMin) : 0;
          const maxPrix = filters.prixMax ? parsePrixUnitaire(filters.prixMax) : Infinity;
          
          console.log(`🔍 Filtrage prix: ${chantier.prixUnitaire} -> ${prix} (min: ${minPrix}, max: ${maxPrix})`);
          
          if (prix < minPrix || prix > maxPrix) {
            console.log(`❌ Prix ${prix} hors limites [${minPrix}, ${maxPrix}] pour ${chantier.nom}`);
            return false;
          }
          console.log(`✅ Prix ${prix} dans les limites [${minPrix}, ${maxPrix}] pour ${chantier.nom}`);
        }

        return true;
      });

      // Retourner l'ouvrage seulement s'il a des chantiers qui correspondent aux filtres
      return filteredChantiers.length > 0;
    }).map(ouvrage => ({
      ...ouvrage,
      chantiers: ouvrage.chantiers.filter(chantier => {
        // Appliquer les mêmes filtres que ci-dessus
        if (filters.localisation && chantier.localisation.toLowerCase() !== filters.localisation.toLowerCase()) {
          return false;
        }
        if (filters.annee && chantier.annee !== filters.annee) {
          return false;
        }
        if (filters.fournisseur && !chantier.fournisseur.toLowerCase().includes(filters.fournisseur.toLowerCase())) {
          return false;
        }
        if (filters.projet && !chantier.nom.toLowerCase().includes(filters.projet.toLowerCase())) {
          return false;
        }
        if (filters.prixMin || filters.prixMax) {
          const prix = parsePrixUnitaire(chantier.prixUnitaire);
          const minPrix = filters.prixMin ? parsePrixUnitaire(filters.prixMin) : 0;
          const maxPrix = filters.prixMax ? parsePrixUnitaire(filters.prixMax) : Infinity;
          
          if (prix < minPrix || prix > maxPrix) {
            return false;
          }
        }
        return true;
      })
    }));
  }, [filters]);

  const toggleOuvrage = (id: number) => {
    const newExpanded = new Set(expandedOuvrages);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedOuvrages(newExpanded);
  };

  const toggleChantier = (id: number) => {
    const newExpanded = new Set(expandedChantiers);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedChantiers(newExpanded);
  };

  const getTotalChantiers = () => {
    return filteredData.reduce((total, ouvrage) => total + ouvrage.chantiers.length, 0);
  };

  const getSourceColor = (source?: string) => {
    switch (source) {
      case 'Facture négociée': return 'bg-green-100 text-green-800';
      case 'Facture non négociée': return 'bg-orange-100 text-orange-800';
      case 'Devis retenu': return 'bg-blue-100 text-blue-800';
      case 'Devis non retenu': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Résultats de la recherche</h2>
        <div className="flex items-center space-x-4">
          {/* Indicateur des filtres de prix actifs */}
          {(filters.prixMin || filters.prixMax) && (
            <div className="flex items-center space-x-2 text-sm text-violet-600 bg-violet-50 px-3 py-1 rounded-full">
              <span>💰 Prix unitaire:</span>
              <span className="font-medium">
                {filters.prixMin ? `${filters.prixMin}€` : '0€'} - {filters.prixMax ? `${filters.prixMax}€` : '∞'}
              </span>
            </div>
          )}
          <span className="text-sm text-gray-500">
            {filteredData.length} ouvrage{filteredData.length > 1 ? 's' : ''} • {getTotalChantiers()} chantier{getTotalChantiers() > 1 ? 's' : ''}
          </span>
        </div>
      </div>
      
      {filteredData.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <div className="text-gray-400 text-lg mb-2">Aucun résultat trouvé</div>
          <div className="text-gray-500 text-sm">Essayez de modifier vos critères de recherche</div>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden bg-white">
          {filteredData.map((ouvrage) => {
            const isOuvrageExpanded = expandedOuvrages.has(ouvrage.id);
            const lastPrice = calculateLastActualizedPrice(ouvrage.chantiers, ouvrage.unite);
            
            return (
              <div key={ouvrage.id} className="border-b last:border-b-0">
                {/* Niveau 1 : Ouvrage */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleOuvrage(ouvrage.id)}
                  onKeyDown={(e) => e.key === 'Enter' && toggleOuvrage(ouvrage.id)}
                  className="flex justify-between items-center px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex-1 text-left">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-medium text-gray-900">{ouvrage.intitule}</h3>
                      <span className="px-2 py-1 text-xs bg-violet-100 text-violet-700 rounded-full">
                        {ouvrage.categorie}
                      </span>
                      <span className="text-sm text-gray-500">({ouvrage.chantiers.length} chantier{ouvrage.chantiers.length > 1 ? 's' : ''})</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Unité : {ouvrage.unite}</p>
                  </div>

                  <div className="flex items-center space-x-6 text-sm">
                    <div className="text-right">
                      <div className="font-medium text-gray-900">{ouvrage.chantiers.length} chantier{ouvrage.chantiers.length > 1 ? 's' : ''}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-violet-600">{lastPrice}</div>
                      <div className="text-gray-500 text-xs">dernier prix</div>
                    </div>
                    {isOuvrageExpanded ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {/* Niveau 2 : Liste des chantiers */}
                {isOuvrageExpanded && (
                  <div className="bg-gray-50 border-t border-gray-200">
                    {ouvrage.chantiers.map((chantier) => {
                      const isChantierExpanded = expandedChantiers.has(chantier.id);
                      
                      return (
                        <div key={chantier.id} className="border-b border-gray-200 last:border-b-0">
                          <button
                            onClick={() => toggleChantier(chantier.id)}
                            className="w-full flex justify-between items-center px-8 py-3 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex-1 text-left">
                              <div className="flex items-center space-x-3">
                                <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                                <h4 className="font-medium text-gray-900">{chantier.nom}</h4>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{chantier.localisation} • {chantier.annee}</p>
                            </div>

                            <div className="flex items-center space-x-6 text-sm">
                              <div className="text-right">
                                <div className="font-medium text-gray-900">{chantier.prixUnitaire}</div>
                                <div className="text-gray-500 text-xs">prix unitaire</div>
                              </div>
                              {chantier.prixActualise && (
                                <div className="text-right">
                                  <div className="font-semibold text-green-600">{chantier.prixActualise}</div>
                                  <div className="text-gray-500 text-xs">prix actualisé</div>
                                </div>
                              )}
                              <div className="text-right">
                                <div className="font-semibold text-violet-600 text-base">{chantier.prixTotal}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-gray-600 text-base">{chantier.quantite}</div>
                              </div>
                              {isChantierExpanded ? (
                                <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </button>
                          
                          {/* Niveau 3 : Détails du chantier */}
                          {isChantierExpanded && (
                            <div className="bg-white px-8 py-4 border-t border-gray-200">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Détails du projet */}
                                <div>
                                  <h5 className="font-medium text-gray-900 mb-3">Détails du projet</h5>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Projet :</span>
                                      <span className="font-medium">{chantier.nom}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Localisation :</span>
                                      <span className="font-medium">{chantier.localisation}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Année :</span>
                                      <span className="font-medium">{chantier.annee}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Fournisseur :</span>
                                      <span className="font-medium">{chantier.fournisseur}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Détails techniques */}
                                <div>
                                  <h5 className="font-medium text-gray-900 mb-3">Détails techniques</h5>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Prix HT unitaire :</span>
                                      <span className="font-medium text-violet-600">{chantier.prixUnitaire}</span>
                                    </div>
                                    {chantier.prixActualise && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Prix actualisé :</span>
                                        <span className="font-semibold text-green-600">{chantier.prixActualise}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Quantité :</span>
                                      <span className="font-medium">{chantier.quantite}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Prix total :</span>
                                      <span className="font-semibold text-violet-600">{chantier.prixTotal}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Unité :</span>
                                      <span className="font-medium">{ouvrage.unite}</span>
                                    </div>
                                    {chantier.source && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Source :</span>
                                        <span className={`px-2 py-1 text-xs rounded-full ${getSourceColor(chantier.source)}`}>
                                          {chantier.source}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Description */}
                              {chantier.description && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h5 className="font-medium text-gray-900 mb-2">Description</h5>
                                  <p className="text-sm text-gray-600">{chantier.description}</p>
                                </div>
                              )}

                              {/* Actions */}
                              <div className="mt-4 flex space-x-3">
                                <button className="px-4 py-2 text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors text-sm font-medium">
                                  Voir le chantier complet
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ResultsTable; 